import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, User, Lock, AlertCircle, ArrowRight, Eye, EyeOff, Shield, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // 2FA challenge state
  const [stage, setStage] = useState('creds'); // 'creds' | '2fa'
  const [tempToken, setTempToken] = useState('');
  const [twoFaCode, setTwoFaCode] = useState('');

  const navigate = useNavigate();

  const finishLogin = (accessToken, user) => {
    localStorage.setItem('user_token', accessToken);
    localStorage.setItem('user_data', JSON.stringify(user || {}));
    navigate('/');
    window.location.reload();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/users/login`, { username, password });
      if (response.data.requires_2fa) {
        setTempToken(response.data.temp_token);
        setStage('2fa');
      } else {
        finishLogin(response.data.access_token, response.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    if (twoFaCode.length !== 6) return;
    setError('');
    setLoading(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/user-2fa/verify-login`, {
        temp_token: tempToken,
        code: twoFaCode,
      });
      finishLogin(r.data.access_token, r.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const backToCreds = () => {
    setStage('creds'); setTwoFaCode(''); setTempToken(''); setError('');
  };

  return (
    <div className="min-h-screen bg-[#05050A] flex items-center justify-center px-6" data-testid="login-page">
      {/* Ambient */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/3 left-1/3 w-[500px] h-[500px] bg-[#00E5FF]/[0.04] rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/3 w-[400px] h-[400px] bg-[#8A2BE2]/[0.04] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 w-full max-w-sm" style={{ animation: 'fadeInUp 0.5s ease-out' }}>
        {stage === 'creds' && (
          <>
            <div className="text-center mb-10">
              <div className="w-14 h-14 bg-gradient-to-br from-[#00E5FF]/20 to-[#8A2BE2]/10 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-white/[0.06]">
                <LogIn className="w-6 h-6 text-[#00E5FF]" />
              </div>
              <h1 className="text-2xl font-light tracking-tight text-white mb-1">Welcome back</h1>
              <p className="text-white/30 text-sm">Sign in to your account</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              {error && (
                <div className="flex items-center gap-2.5 p-3.5 rounded-xl bg-red-500/5 border border-red-500/15" data-testid="login-error">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <span className="text-red-400 text-xs">{error}</span>
                </div>
              )}
              <div>
                <label className="block text-[11px] text-white/30 mb-2 uppercase tracking-wider font-medium">Username</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
                  <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus
                    data-testid="login-username-input" placeholder="Enter username"
                    className="w-full pl-10 pr-4 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-[#00E5FF]/40 focus:border-[#00E5FF]/30 transition-all text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-white/30 mb-2 uppercase tracking-wider font-medium">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required
                    data-testid="login-password-input" placeholder="Enter password"
                    className="w-full pl-10 pr-11 py-3 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-[#00E5FF]/40 focus:border-[#00E5FF]/30 transition-all text-sm" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/50 transition-colors">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                className="w-full bg-white text-black hover:bg-white/90 font-medium py-3 rounded-xl text-sm transition-all disabled:opacity-30">
                {loading ? 'Signing in...' : 'Sign In'}
                {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </form>

            <div className="mt-8 text-center space-y-3">
              <p className="text-white/20 text-xs">
                Don't have an account?{' '}
                <Link to="/signup" className="text-[#00E5FF] hover:text-[#00E5FF]/80 transition-colors font-medium" data-testid="goto-signup-link">
                  Sign Up
                </Link>
              </p>
              <Link to="/" className="block text-white/15 hover:text-white/30 text-xs transition-colors">
                Back to website
              </Link>
            </div>
          </>
        )}

        {stage === '2fa' && (
          <div data-testid="login-2fa-stage">
            <div className="text-center mb-8">
              <div className="w-14 h-14 bg-gradient-to-br from-emerald-500/20 to-[#00E5FF]/10 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-emerald-500/20">
                <Shield className="w-6 h-6 text-emerald-300" />
              </div>
              <h1 className="text-2xl font-light tracking-tight text-white mb-1">Two-factor authentication</h1>
              <p className="text-white/30 text-sm">Enter the 6-digit code from your authenticator app</p>
            </div>

            <form onSubmit={handle2FA} className="space-y-5">
              {error && (
                <div className="flex items-center gap-2.5 p-3.5 rounded-xl bg-red-500/5 border border-red-500/15" data-testid="login-2fa-error">
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                  <span className="text-red-400 text-xs">{error}</span>
                </div>
              )}

              <div>
                <label className="block text-[11px] text-white/30 mb-2 uppercase tracking-wider font-medium flex items-center gap-2">
                  <KeyRound className="w-3 h-3" /> Verification code
                </label>
                <input
                  type="text" inputMode="numeric" autoFocus maxLength={6}
                  value={twoFaCode}
                  onChange={(e) => setTwoFaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  data-testid="login-2fa-input"
                  className="w-full text-center text-2xl tracking-[0.6em] font-mono px-4 py-4 bg-white/[0.03] border border-white/[0.08] rounded-xl text-white placeholder-white/15 focus:outline-none focus:ring-1 focus:ring-emerald-400/40 focus:border-emerald-400/30 transition-all"
                  placeholder="000000"
                />
              </div>

              <Button type="submit" disabled={twoFaCode.length !== 6 || loading} data-testid="login-2fa-submit-btn"
                className="w-full bg-emerald-500 text-black hover:bg-emerald-400 font-medium py-3 rounded-xl text-sm transition-all disabled:opacity-30">
                {loading ? 'Verifying…' : 'Verify & sign in'}
              </Button>

              <button type="button" onClick={backToCreds} className="w-full text-white/30 hover:text-white/60 text-xs transition-colors">
                Sign in with a different account
              </button>
            </form>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Login;
